import { useEffect, useRef } from "react";
import { disableNetwork, enableNetwork } from "firebase/firestore";
import { db } from "../firebase";
import { useOnlineStatus } from "./useOnlineStatus";

/**
 * Bridges the reactive online status into Firestore's network state.
 *
 * Why: When the browser reports online but real connectivity is flaky
 * (captive portal, dead wifi), Firestore's WebChannel transport hangs
 * waiting for its ~60s long-polling timeout before any query resolves.
 * Calling `disableNetwork` makes all pending + subsequent reads fall
 * through to the IndexedDB cache immediately. When real connectivity
 * comes back, `enableNetwork` resumes live sync.
 *
 * Should be mounted exactly once, app-wide (from Layout).
 */
export function useFirestoreNetworkBridge(): void {
  const isOnline = useOnlineStatus();
  const lastAppliedRef = useRef<boolean | null>(null);

  useEffect(() => {
    // Dedupe: only call Firestore APIs when the state actually flips.
    if (lastAppliedRef.current === isOnline) return;
    lastAppliedRef.current = isOnline;

    let cancelled = false;

    const apply = async () => {
      try {
        if (isOnline) {
          await enableNetwork(db);
        } else {
          await disableNetwork(db);
        }
      } catch {
        // If toggling fails (e.g. during teardown), reset so we retry
        // on the next flip.
        if (!cancelled) {
          lastAppliedRef.current = null;
        }
      }
    };

    void apply();

    return () => {
      cancelled = true;
    };
  }, [isOnline]);
}
